<template>
  <div id="sitemap-adaptations" class="sitemap container">
    <header>
      <h1 class="gb-page-title text-center">UDL-Aligned Strategies</h1>
      <h2>What are UDL-aligned strategies?</h2>
      <div class="row">
        <div class="col-sm-9 col-md-8">
          <p class="lead">UDL-aligned strategies are instructional methods and tools used by teachers to ensure that ALL students have an equal opportunity to learn. All of our strategies are aligned with <a
            class="bold-link"
            :href="aboutUDLLink"
            title="About Universal Design for Learning"
            target="_blank">Universal Design for Learning (UDL) <OpensInNewTabIcon :external-link="true" /></a> guidelines. These guidelines help you to select strategies that remove barriers in instruction so that all students can achieve their learning goals.</p>
        </div>
        <div class="col-sm-3 col-md-4 text-center">
          <a
            href="http://udlguidelines.cast.org"
            title="UDL Guidelines"
            target="_blank"
            class="btn btn-lg btn-link">
            <UdlBrainNetworkInfographic class="infographic" />
            Learn more about UDL
          </a>
        </div>
      </div>
    </header>

    <main>
      <p v-if="fetching" class="text-center lead text-muted"><i class="fa fa-circle-o-notch fa-spin"/> Loading strategies...</p>

      <div v-if="!fetching" class="collapse-expand-actions text-right text-muted">
        <small>
          <a
            @click="collapseAll"
            @keyup.enter="collapseAll"
            role="button"
            tabindex="0">
            Collapse all
          </a> | <a
            @click="expandAll"
            @keyup.enter="expandAll"
            role="button"
            tabindex="0">
            Expand all
          </a>
        </small>
      </div>
      <div class="row" v-if="!fetching">
        <div
          class="col-md-4"
          v-for="principleNum of ['III', 'I', 'II']"
          :key="principleNum">
          <h3 class="principle-header" :class="principleHeaderClass(principleNum)">Strategies for {{ principleMappings[principleNum] }}</h3>

          <UdlGuidelineStrategyPanel
            v-for="guideline of guidelinesForPrinciple(principleNum)"
            :key="guideline.label"
            :title="guideline.label"
            :principle="principleMappings[principleNum]"
            :checkpoints="guideline.checkpoints" />
        </div>
      </div>
    </main>

    <SubmitFeedback type="UDL Strategy Index" />

  </div>
</template>

<script>
import { mapState, mapActions, mapGetters } from 'vuex'
import UdlGuidelineStrategyPanel from './UdlGuidelineStrategyPanel'
import SubmitFeedback from '@/components/SubmitFeedback'
import PopoverMixin from '@/mixins/PopoverMixin'
import UdlBrainNetworkInfographic from './UdlBrainNetworkInfographic.vue'
import OpensInNewTabIcon from '@/components/OpensInNewTabIcon.vue'
import { goalbookUrls } from '@/links'

export default {
  name: 'StrategyIndexPage',
  components: { UdlGuidelineStrategyPanel, SubmitFeedback, UdlBrainNetworkInfographic, OpensInNewTabIcon },
  data() {
    return {
      errorMessage: '',
      aboutUDLLink: goalbookUrls.aboutUDL,
    }
  },
  mixins: [PopoverMixin],
  computed: {
    ...mapState('udlIndex', ['fetching', 'principleMappings', 'eventHub']),
    ...mapGetters('udlIndex', ['guidelinesForPrinciple'])
  },
  methods: {
    ...mapActions('udlIndex', ['getUdlIndexStrategies']),
    principleHeaderClass(principleNum) {
      return `udl-${this.principleMappings[principleNum].toLowerCase().replace(/[^A-Za-z0-9]+/g, '-')}-light`
    },
    collapseAll() {
      this.eventHub.$emit('expand', false)
    },
    expandAll() {
      this.eventHub.$emit('expand', true)
    }
  },
  created() {
    this.getUdlIndexStrategies()
    this.$sendEvent('Viewed Strategy Index', {
      ref: this.$route.query.ref
    })
  }
}
</script>

<style scoped lang="less">
@import '~@/variables.less';

#sitemap-adaptations {
  img {
    display: block;
    max-width: 100%;
    margin-bottom: 20px;
  }

  .collapse-expand-actions {
    margin-top: 24px;
    margin-bottom: 0;
  }

  .principle-header {
    font-weight: 200;
    font-size: 24px;
    line-height: 1.61em;
    padding: 6px 12px;
    border: 0;
    border-radius: 4px;
  }

  .udl-representation-light {
    background-color: @udlRepresentationLight;
  }
  .udl-action-expression-light {
    background-color: @udlExpressionLight;
  }
  .udl-engagement-light {
    background-color: @udlEngagementLight;
  }
  .bold-link {
    font-weight: 400;
    color: #255A88;
  }
  .infographic {
    margin-bottom: var(--spacing-lg);
  }
  .btn-link {
    text-decoration: none;
  }
}
</style>
